import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  Box,
  Menu,
  FormControlLabel,
  Checkbox,
  TextField,
  Typography,
  Card,
  CardContent,
  Breadcrumbs,
  Stack,
  CircularProgress,
  Alert,
  Fade,
  Paper,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogContent,
  Switch,
  TableSortLabel,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import { Link } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { BUTTON_STYLES } from "../../styles/componentStyles";
import NavBar from "../NavBar";
import api from "../../axiosInstance";
import { useTheme } from "@emotion/react";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteConfirmationDialog from "../DeleteConfirmationDialog";
import { toast, ToastContainer } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import FaqModal from "./FaqModal";

// Column config and visibility state
const allColumns = [
  {
    key: "id",
    label: "ID",
    cellCss: { align: "center", minWidth: "50px" },
    getValue: (r) => r.id,
  },
  {
    key: "category",
    label: "Category",
    cellCss: { align: "center", minWidth: "120px" },
    getValue: (r) => r.category,
  },
  {
    key: "question",
    label: "Question",
    cellCss: { align: "center", minWidth: "120px" },
    getValue: (r) => r.question,
  },
  {
    key: "answer",
    label: "Answer",
    cellCss: { align: "center", minWidth: "200px" },
    getValue: (r) => r.answer,
  },
  {
    key: "universityName",
    label: "University",
    cellCss: { align: "center", minWidth: "150px" },
    getValue: (r) =>
      r.universityId == null ? "All universities" : r.universityName || `#${r.universityId}`,
  },
  {
    key: "page",
    label: "Page",
    cellCss: { align: "center", minWidth: "120px" },
    getValue: (r) => r.page,
  },
  {
    key: "displayOrder",
    label: "DisplayOrder",
    cellCss: { align: "center", minWidth: "150px" },
    getValue: (r) => r.displayOrder,
  },
  {
    key: "active",
    label: "Active",
    getValue: (r) => (r.active ? "Yes" : "No"),
  },
];
const defaultVisibleKeys = new Set([
  "id",
  "category",
  "question",
  "answer",
  "universityName",
  "page",
  "active",
]);

// v2: the University column was added; saved column choices from before it
// would otherwise keep it hidden.
const STORAGE_KEY = "faqVisibleColumns.v2";

function FaqTable() {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fqa, setFaq] = useState([]);
  const [universities, setUniversities] = useState([]);
  // University filter: "" = all universities (every FAQ).
  const [selectedUniversity, setSelectedUniversity] = useState("");

  const [openNew, setOpenNew] = useState(false);
  const handleNewClose = () => setOpenNew(false);
  const [selectedstates, setSelectedStates] = useState("1" || "");
  const [searchQuery, setSearchQuery] = useState(() => {
    return localStorage.getItem("faq_management") || "";
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [openImage, setOpenImage] = useState(null);
  const [faqEdit, setFaqEdit] = useState(null);
  const [dense, setDense] = useState(true);
  const [orderBy, setOrderBy] = useState("category");
  const [order, setOrder] = useState("asc");

  //  Filter
  const filteredFaq = fqa.filter(
    (faq) =>
      !searchQuery ||
      faq.question?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.page?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  // Inside sorted Data your component
  const sortedData = useMemo(() => {
    if (!filteredFaq) return [];

    return [...filteredFaq].sort((a, b) => {
      if (!orderBy) return 0;

      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === "string") {
        return order === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });
  }, [filteredFaq, orderBy, order]);
  // Save to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("faq_management", searchQuery);
  }, [searchQuery]);
  // Sorting logic
  const handleSort = (columnKey) => {
    const isAsc = orderBy === columnKey && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnKey);
  };

  // padding Dense
  const handleChangeDense = (event) => {
    setDense(event.target.checked);
  };
  const handleChange = (event) => {
    setSelectedStates(event.target.value);
  };
  console.log(fqa);

  // Universities for the filter and the Add/Edit popup.
  useEffect(() => {
    api
      .get("/getAllUniversities")
      .then((res) => setUniversities(res.data.data || []))
      .catch((error) => console.error("Failed to load universities", error));
  }, []);

  //Fetching the fqa data, for one university when the filter is set
  useEffect(() => {
    setIsLoading(true);
    api
      .get(
        `/api/v1/faqs`,
        selectedUniversity ? { params: { universityId: selectedUniversity } } : undefined
      )
      .then((res) => {
        setFaq(res.data.data);
      })

      .catch((error) => {
        console.error("There was an error fetching the book details!", error);
        setError("Failed to load books. Please try again.");
      })
      .finally(() => setIsLoading(false));
  }, [selectedUniversity]);

  // Initialize from localStorage synchronously to avoid a race with the persister effect
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) {
          const validKeys = new Set(allColumns.map((c) => c.key));
          const filtered = arr.filter((k) => validKeys.has(k));
          if (filtered.length > 0) {
            return new Set(filtered);
          }
        }
      }
    } catch {}
    return new Set(Array.from(defaultVisibleKeys));
  });

  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const openColumnsMenu = Boolean(columnsMenuAnchor);

  // Persist column config to localStorage on change
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(Array.from(visibleColumns))
      );
    } catch {}
  }, [visibleColumns]);

  const handleOpenColumnsMenu = (e) => setColumnsMenuAnchor(e.currentTarget);
  const handleCloseColumnsMenu = () => setColumnsMenuAnchor(null);
  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Helper function to get boolean chip styling
  const getBooleanChip = (value) => {
    const normalized = value === true || value === "true" || value === "Yes";
    return (
      <Chip
        label={normalized ? "Yes" : "No"}
        color={normalized ? "success" : "error"}
        size="small"
        variant="outlined"
      />
    );
  };

  // Helper function to get content type chip styling
  const getContentTypeChip = (type) => {
    const typeConfig = {
      text: { color: "primary", label: "Text" },
      image: { color: "secondary", label: "Image" },
      video: { color: "success", label: "Video" },
      link: { color: "info", label: "Link" },
    };

    const config = typeConfig[type?.toLowerCase()] || {
      color: "default",
      label: type || "Unknown",
    };
    return <Chip label={config.label} color={config.color} size="small" />;
  };
  //   Page nation handel changes
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // page nation rows handles
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
  };
  // pagenation code
  const paginatedFAQ = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Columns Field showing obeject
  const visibleColumnDefs = allColumns.filter((c) => visibleColumns.has(c.key));
  // Deletion confirm dialog box open and close.
  const handleClickOpen = (id) => {
    setSelectedId(id);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedId(null);
  };
  // Delete The banner record api fetch data
  const handleDelete = async () => {
    try {
      await api.delete(`/api/v1/faqs/${selectedId}`);

      // If deletion is successful, update local state update
      setFaq((prev) => prev.filter((row) => row.id !== selectedId));
      toast.success("Record deleted successfully.!", {
        position: "top-right",
        autoClose: 3000,
      });
      setOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete the record. Please try again.");
    }
  };
  // opening Adding page data in onclick
  const handleNewOpen = () => {
    setOpenNew(true);
    setFaqEdit([]);
  };
  // Editing page data in onclick
  const handleEditClick = (rowData) => {
    handleNewOpen();
    setFaqEdit(rowData);
  };
  const handleFormSuccess = (newItem) => {
    if (!newItem || !newItem.id) {
      console.warn("Invalid item received from form", newItem);
      return;
    }
    // Moved out of the university being shown (e.g. made global): drop it.
    if (selectedUniversity && newItem.universityId !== Number(selectedUniversity)) {
      setFaq((prev) => prev.filter((item) => item.id !== newItem.id));
      setFaqEdit(null);
      return;
    }
    setFaq((prev) => {
      const exists = prev.some((item) => item.id === newItem.id);

      if (exists) {
        return prev.map((item) => (item.id === newItem.id ? newItem : item));
      } else {
        return [newItem, ...prev];
      }
    });

    setFaqEdit(null);
  };
  return (
    <>
      {/* <NavBar /> */}
      <ToastContainer />
      {/* <Card sx={{ mb: 3, boxShadow: 2, width: "100%", mt: 2 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box>
              <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                <Link underline="hover" color="inherit" to="/booklist">
                  Books List
                </Link>
                <Typography sx={{ color: "text.primary", fontWeight: 600 }}>
                  FAQ Management
                </Typography>
              </Breadcrumbs>
              <Typography
                variant="h4"
                component="h1"
                sx={{ fontWeight: 600, color: "primary.main" }}
              >
                FAQ
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => window.location.reload()}
              disabled={isLoading}
              sx={{ minWidth: 120 }}
            >
              {isLoading ? <CircularProgress size={20} /> : "Refresh"}
            </Button>
          </Box>

          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleNewOpen}
              {...BUTTON_STYLES.primary}
            >
              Add FAQ
            </Button>
            <Button
              variant="outlined"
              startIcon={<ViewColumnIcon />}
              onClick={handleOpenColumnsMenu}
              sx={{ minWidth: 120 }}
            >
              Columns
            </Button>
            <Menu
              anchorEl={columnsMenuAnchor}
              open={openColumnsMenu}
              onClose={handleCloseColumnsMenu}
            >
              {allColumns.map((col) => (
                <FormControlLabel
                  key={col.key}
                  control={
                    <Checkbox
                      checked={visibleColumns.has(col.key)}
                      onChange={() => toggleColumn(col.key)}
                    />
                  }
                  label={col.label}
                  sx={{ pl: 1, pr: 2 }}
                />
              ))}
            </Menu>
          </Stack>
        </CardContent>
      </Card> */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" }, // column on mobile, row on desktop
          justifyContent: { xs: "flex-start", md: "space-between" }, // start on mobile, space-between on desktop
          alignItems: { xs: "stretch", md: "center" }, // stretch on mobile, center on desktop
          gap: { xs: 1, md: 0 }, // spacing between items
          mb: 2,
          marginLeft: "10px",
          marginTop: "10px",
        }}
      >
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
          <Link underline="hover" color="inherit" to="/booklist">
            Books List
          </Link>
          <Typography sx={{ color: "text.primary", fontWeight: 600 }}>
            FAQ Management
          </Typography>
        </Breadcrumbs>

        <Box>
          {/* Action Buttons */}
          <Stack
            direction="row"
            spacing={2}
            flexWrap="wrap"
            useFlexGap
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: { xs: "wrap", md: "nowrap" }, // wrap buttons on mobile
              mt: { xs: 1, md: 0 },
              marginRight: "10px",
            }}
          >
            <Button
              variant="outlined"
              startIcon={<ViewColumnIcon />}
              onClick={handleOpenColumnsMenu}
              sx={{
                minWidth: { xs: "120px", md: "auto" }, // smaller on mobile
                fontSize: { xs: "10px", md: "12px" },
              }}
            >
              Columns
            </Button>
            <Menu
              anchorEl={columnsMenuAnchor}
              open={openColumnsMenu}
              onClose={handleCloseColumnsMenu}
              PaperProps={{
                sx: {
                  width: 200,
                  maxHeight: 500,
                  overflowY: "auto",
                  borderRadius: 2,
                },
              }}
            >
              {allColumns.map((col) => (
                <FormControlLabel
                  key={col.key}
                  control={
                    <Checkbox
                      checked={visibleColumns.has(col.key)}
                      onChange={() => toggleColumn(col.key)}
                    />
                  }
                  label={col.label}
                  sx={{ pl: 1, pr: 2 }}
                />
              ))}
            </Menu>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleNewOpen}
              {...BUTTON_STYLES.primary}
              sx={{
                minWidth: { xs: "120px", md: "auto" }, // smaller on mobile
                fontSize: { xs: "10px", md: "12px" },
              }}
            >
              Add FAQ
            </Button>
          </Stack>
        </Box>
      </Box>
      {/* Search Section */}
      <Card sx={{ margin: "0 15px", boxShadow: 1 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                minWidth: { xs: "80%", md: "50%" },
                display: "flex",
                width: "40%",
                alignItems: "center",
                gap: 2,
              }}
            >
              <TextField
                size="small"
                fullWidth
                variant="outlined"
                placeholder="Search with Category, Question and Page..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ maxWidth: 400 }}
              />
              <TextField
                select
                size="small"
                label="University"
                value={selectedUniversity}
                onChange={(e) => {
                  setSelectedUniversity(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="">All Universities</MenuItem>
                {universities.map((university) => (
                  <MenuItem value={university.id} key={university.id}>
                    {university.name}
                  </MenuItem>
                ))}
              </TextField>
              <Typography
                sx={{ display: { xs: "none", md: "block" } }}
                variant="body2"
                color="text.secondary"
              >
                {
                  sortedData.filter(
                    (banner) =>
                      banner.question
                        ?.toLowerCase()
                        .includes(searchQuery.toLowerCase()) ||
                      banner.category
                        ?.toLowerCase()
                        .includes(searchQuery.toLowerCase()) ||
                      banner.page
                        ?.toLowerCase()
                        .includes(searchQuery.toLowerCase())
                  ).length
                }{" "}
                of {fqa.length} FAQ items
              </Typography>
            </Box>
            <Box>
              {/* Dense toggle */}
              <Tooltip title="Dense padding" arrow>
                <FormControlLabel
                  control={
                    <Switch
                      checked={dense}
                      onChange={handleChangeDense}
                      color="primary"
                    />
                  }
                />
              </Tooltip>
            </Box>
          </Box>
          <Typography
            sx={{ display: { xs: "block", md: "none" }, textAlign: "left" }}
            variant="body2"
            color="text.secondary"
          >
            {
              sortedData.filter(
                (banner) =>
                  banner.question
                    ?.toLowerCase()
                    .includes(searchQuery.toLowerCase()) ||
                  banner.category
                    ?.toLowerCase()
                    .includes(searchQuery.toLowerCase()) ||
                  banner.page?.toLowerCase().includes(searchQuery.toLowerCase())
              ).length
            }{" "}
            of {fqa.length} FAQ items
          </Typography>
        </CardContent>
      </Card>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {!isLoading && (
        <Fade in={!isLoading}>
          <Paper
            sx={{
              width: "98%",
              mx: "auto",
              overflow: "hidden",
              boxShadow: 2,
              marginBottom: "20px",
            }}
          >
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table
                stickyHeader
                aria-label="news table"
                size={dense ? "small" : "medium"}
              >
                <TableHead>
                  <TableRow>
                    {visibleColumnDefs.map((c) => (
                      <TableCell
                        key={c.key}
                        sx={{
                          padding: "10px 5px ",
                          backgroundColor: "#17479e",
                          color: "white",
                          fontWeight: 600,
                          ...c.cellCss,
                          position: "sticky",
                          top: 0,
                          zIndex: 3,
                        }}
                        sortDirection={orderBy === c.key ? order : false}
                      >
                        <TableSortLabel
                          active={orderBy === c.key}
                          direction={orderBy === c.key ? order : "asc"}
                          onClick={() => handleSort(c.key)}
                          sx={{
                            color: "#ffffff !important",
                            "&.Mui-active": {
                              color: "#ffffff !important",
                            },
                            "& .MuiTableSortLabel-icon": {
                              color: "#ffffff !important",
                            },
                          }}
                        >
                          {c.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell
                      sx={{
                        backgroundColor: "#17479e",
                        color: "white",
                        fontWeight: 600,
                        minWidth: 120,
                        textAlign: "center",
                        position: "sticky",
                        top: 0,
                        right: 0,
                        zIndex: 4,
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(paginatedFAQ) && paginatedFAQ.length > 0 ? (
                    paginatedFAQ
                      .filter(
                        (faq) =>
                          !searchQuery ||
                          faq.question
                            ?.toLowerCase()
                            .includes(searchQuery.toLowerCase()) ||
                          faq.category
                            ?.toLowerCase()
                            .includes(searchQuery.toLowerCase()) ||
                          faq.page
                            ?.toLowerCase()
                            .includes(searchQuery.toLowerCase())
                      )
                      .map((row) => (
                        <TableRow
                          hover
                          role="checkbox"
                          tabIndex={-1}
                          key={row.id}
                          sx={{
                            "&:nth-of-type(odd)": {
                              backgroundColor: "rgba(0, 0, 0, 0.02)",
                            },
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)",
                            },
                          }}
                        >
                          {visibleColumnDefs.map((c) => (
                            <TableCell
                              key={c.key}
                              sx={{ padding: "10px 10px", ...c.cellCss }}
                            >
                              {(() => {
                                const value = c.getValue(row);
                                const key = c.key;

                                // Optional debug

                                if (key === "active" || key === "isNational") {
                                  return getBooleanChip(value);
                                }

                                if (key === "contentType") {
                                  return getContentTypeChip(value);
                                }

                                if (key === "image") {
                                  return key === "image" ? (
                                    <img
                                      src={value}
                                      alt="preview"
                                      style={{
                                        width: "60px",
                                        height: "auto",
                                        borderRadius: "4px",
                                        cursor: "pointer",
                                      }}
                                      onClick={() => setOpenImage(value)}
                                    />
                                  ) : (
                                    value // show fallback text if not valid image
                                  );
                                }

                                return value;
                              })()}
                            </TableCell>
                          ))}
                          <TableCell
                            sx={{
                              align: "center",
                              padding: "10px 10px",
                              minWidth: "120px",
                              position: "sticky",
                              right: 0,
                              backgroundColor: "#fff",
                              zIndex: 1,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={0.5}
                              justifyContent="center"
                            >
                              <Tooltip title="Edit FAQ" arrow placement="top">
                                <IconButton
                                  color="primary"
                                  onClick={() => handleEditClick(row)}
                                  size="small"
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: "primary.light",
                                      color: "white",
                                    },
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete FAQ" arrow placement="top">
                                <IconButton
                                  color="error"
                                  onClick={() => handleClickOpen(row.id)}
                                  size="small"
                                  sx={{
                                    "&:hover": {
                                      backgroundColor: "error.light",
                                      color: "white",
                                    },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={visibleColumnDefs.length + 1}
                        sx={{ textAlign: "center", py: 4 }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Typography variant="h6" color="text.secondary">
                            {searchQuery
                              ? "No news found matching your search"
                              : "No news available"}
                          </Typography>
                          {searchQuery && (
                            <Button
                              variant="outlined"
                              onClick={() => setSearchQuery("")}
                              startIcon={<SearchIcon />}
                            >
                              Clear Search
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 30, 50, 100]}
              component="div"
              count={filteredFaq.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: "1px solid",
                borderColor: "divider",
                "& .MuiTablePagination-toolbar": {
                  paddingLeft: 2,
                  paddingRight: 2,
                },
              }}
            />
          </Paper>
        </Fade>
      )}
      {/* Banner modal */}
      <FaqModal
        open={openNew}
        onClose={handleNewClose}
        faqEdit={faqEdit}
        onSuccess={handleFormSuccess}
        universities={universities}
        defaultUniversityId={selectedUniversity}
      />
      {/* Confirm Delete Dialog */}
      <DeleteConfirmationDialog
        open={open}
        onClose={handleClose}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this news item? This action cannot be undone."
        confirmText="DELETE"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
      <Dialog
        open={!!openImage}
        onClose={() => setOpenImage(null)}
        maxWidth="md"
      >
        <DialogContent>
          <Box position="relative">
            <IconButton
              aria-label="close"
              onClick={() => setOpenImage(null)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
            <img
              src={openImage}
              alt="Preview"
              style={{ width: "100%", height: "auto", borderRadius: 4 }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default FaqTable;
